import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  ActionButtons,
  Button,
  DataTable,
  InputField,
  SearchWithFilter,
  Select,
  ToggleSwitch,
  type DataTableColumn,
  type DataTableSortDirection,
  type FilterSelection,
} from '@enamelai/design-system'
import { Icon as CheckmarkIcon } from '@enamelai/design-system/icons/Checkmark'
import { Icon as CloseIcon } from '@enamelai/design-system/icons/Close'
import { DeleteConfirmationModal } from '@components/layout/Modals'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import { useDebouncedValue } from '@hooks/useDebouncedValue'
import { useNotificationToast } from '@context/NotificationToastContext'
import { useSettingsHeader } from '@pages/Settings/SettingsHeader.context'
import { useSettingsSubpageLoading } from '@pages/Settings/SettingsSubpageGate.component'
import { SettingStatus } from '@dataTypes/settingStatus.type'
import { SortDirection } from '@constants/Common/sort-direction.constant'
import SettingsRowActions from '../../shared/SettingsRowActions.component'
import { SETTINGS_PAGE_SIZE_OPTIONS } from '../../shared/settingsTable'
import useAdjustments from './useAdjustments'
import {
  ADJUSTMENT_FILTER_LABELS,
  ADJUSTMENT_FILTERS,
} from '../shared/billingSettings.constants'
import { DisplayAdjustmentType } from '../shared/billingSettings.types'
import './AdjustmentsStyle.css'

const ADJUSTMENTS_SEARCH_DEBOUNCE_MS = 300
const NEW_ROW_ID = 'new-adjustment-type'
const EMPTY_FILTERS: FilterSelection = {
  category: [],
  modifier: [],
  status: [],
}

const MODIFIER_OPTIONS = [
  { value: 'Positive', label: 'Positive (+)' },
  { value: 'Negative', label: 'Negative (-)' },
]

const CATEGORY_OPTIONS = [
  { value: 'Adjustment', label: 'Adjustment' },
  { value: 'Collection', label: 'Collection' },
]

type AdjustmentRow = {
  id: string
  adjustmentTypeId: string
  status: string
  name: string
  modifier: string
  category: string
  isEditing: boolean
}

type EditableField = 'status' | 'name' | 'modifier' | 'category'

const Adjustments = (): React.ReactElement => {
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const { showToast } = useNotificationToast()
  const { setHeaderButtons } = useSettingsHeader()
  const [searchQuery, setSearchQuery] = useState('')
  const debouncedSearchQuery = useDebouncedValue(
    searchQuery,
    ADJUSTMENTS_SEARCH_DEBOUNCE_MS,
  )
  const [filters, setFilters] = useState<FilterSelection>(EMPTY_FILTERS)
  const [sort, setSort] = useState<{
    columnId: string
    direction: DataTableSortDirection
  }>({ columnId: 'name', direction: 'asc' })
  const [pendingDelete, setPendingDelete] = useState<AdjustmentRow | null>(null)

  const hooks = useAdjustments(
    debouncedSearchQuery,
    filters,
    showToast,
    showLoading,
    hideLoading,
  )
  const { reset, listLoading } = hooks
  useSettingsSubpageLoading(listLoading)
  const hasSearchOrFilter =
    searchQuery.trim().length > 0 ||
    Object.values(filters).some((values) => (values?.length ?? 0) > 0)
  const isTrulyEmpty =
    !listLoading &&
    !hooks.newEntry &&
    hooks.adjustmentTypes.length === 0 &&
    !hasSearchOrFilter

  useEffect(() => {
    reset()
  }, [reset])

  const rows: AdjustmentRow[] = useMemo(
    () =>
      hooks.adjustmentTypes.map((entry: DisplayAdjustmentType) => ({
        id: entry.adjustment_type_id || NEW_ROW_ID,
        adjustmentTypeId: entry.adjustment_type_id,
        status: entry.status,
        name: entry.name,
        modifier: entry.modifier,
        category: entry.category,
        isEditing: entry.isEditing,
      })),
    [hooks.adjustmentTypes],
  )

  const editingRows = rows.filter((row) => row.isEditing)
  const isEditing = editingRows.length > 0
  const canSaveAll = editingRows.every((row) => row.name.trim().length > 0)

  const editActionsRef = useRef({
    cancelAll: (): void => undefined,
    saveAll: async (): Promise<void> => undefined,
  })
  editActionsRef.current = {
    cancelAll: () => {
      editingRows.forEach((row) => hooks.handleCancel(row.adjustmentTypeId))
    },
    saveAll: async () => {
      for (const row of editingRows) {
        await hooks.handleSave(row.adjustmentTypeId)
      }
    },
  }

  const { handleAddNew } = hooks

  useEffect(() => {
    setHeaderButtons(
      isEditing ? (
        <>
          <Button
            className="settings-ds-button"
            variant="secondary"
            size="md"
            type="button"
            onClick={() => editActionsRef.current.cancelAll()}
          >
            Cancel
          </Button>
          <Button
            className="settings-ds-button"
            variant="primary"
            size="md"
            type="button"
            disabled={!canSaveAll}
            onClick={() => void editActionsRef.current.saveAll()}
          >
            Save changes
          </Button>
        </>
      ) : (
        <>
          <SearchWithFilter
            className="adjustments-search"
            placeholder="Search"
            value={searchQuery}
            onChange={setSearchQuery}
            filters={ADJUSTMENT_FILTERS}
            filterLabels={ADJUSTMENT_FILTER_LABELS}
            selectedFilters={filters}
            onFilterChange={setFilters}
            showResetButton
            onReset={() => {
              setSearchQuery('')
              setFilters(EMPTY_FILTERS)
            }}
          />
          {!isTrulyEmpty && (
            <Button
              className="settings-ds-button"
              variant="primary"
              size="md"
              type="button"
              onClick={handleAddNew}
            >
              Add adj. type
            </Button>
          )}
        </>
      ),
    )
    return () => setHeaderButtons(null)
  }, [
    canSaveAll,
    filters,
    handleAddNew,
    isEditing,
    isTrulyEmpty,
    searchQuery,
    setHeaderButtons,
  ])

  const handleSort = (columnId: string): void => {
    const direction: DataTableSortDirection =
      sort.columnId === columnId && sort.direction === 'asc' ? 'desc' : 'asc'
    setSort({ columnId, direction })
    hooks.handleTableSortChange({
      key: columnId,
      direction: direction === 'asc' ? SortDirection.ASC : SortDirection.DESC,
    })
  }

  const changeField = (
    row: AdjustmentRow,
    field: EditableField,
    value: string,
  ): void => hooks.handleChange(row.adjustmentTypeId, field, value)

  const columns: DataTableColumn<AdjustmentRow>[] = [
    {
      id: 'status',
      label: 'Status',
      sortable: true,
      width: 88,
      renderCell: (row) => (
        <ToggleSwitch
          aria-label={`${row.name || 'Adjustment type'} status`}
          checked={row.status === SettingStatus.Enabled}
          onChange={(checked) =>
            row.isEditing
              ? changeField(
                  row,
                  'status',
                  checked ? SettingStatus.Enabled : SettingStatus.Disabled,
                )
              : hooks.handleToggleStatus(row.adjustmentTypeId, checked)
          }
        />
      ),
    },
    {
      id: 'name',
      label: 'Adjustment type name',
      sortable: true,
      renderCell: (row) =>
        row.isEditing ? (
          <InputField
            className="adjustments-inline-field"
            variant="text"
            aria-label="Adjustment type name"
            placeholder="Enter name"
            clearable={false}
            fullWidth
            value={row.name}
            onChange={(e) => changeField(row, 'name', e.target.value)}
          />
        ) : (
          row.name
        ),
    },
    {
      id: 'modifier',
      label: 'Modifier',
      sortable: true,
      showInfo: true,
      renderCell: (row) =>
        row.isEditing ? (
          <Select
            className="adjustments-inline-field"
            label="Modifier"
            placeholder="Modifier type"
            options={MODIFIER_OPTIONS}
            value={row.modifier}
            onChange={(value) => changeField(row, 'modifier', String(value))}
          />
        ) : (
          <span className="adjustments-modifier">
            {row.modifier}
            <span className="adjustments-modifier__sign">
              {row.modifier === 'Positive' ? '(+)' : '(-)'}
            </span>
          </span>
        ),
    },
    {
      id: 'category',
      label: 'Category',
      sortable: true,
      renderCell: (row) =>
        row.isEditing ? (
          <Select
            className="adjustments-inline-field"
            label="Category"
            placeholder="Category type"
            options={CATEGORY_OPTIONS}
            value={row.category}
            onChange={(value) => changeField(row, 'category', String(value))}
          />
        ) : (
          row.category
        ),
    },
  ]

  const tableState = listLoading
    ? 'loading'
    : rows.length === 0
      ? 'empty'
      : 'default'

  return (
    <div className="settings-accounting-adjustments">
      <DataTable<AdjustmentRow>
        className="adjustments-table"
        columns={columns}
        rows={rows}
        state={tableState}
        emptyState={{
          title: 'No adjustment types found',
          description: hasSearchOrFilter
            ? 'No adjustment types match your search.'
            : "You don't have any adjustment types on record yet.",
          actionLabel: !hasSearchOrFilter ? 'Add adj. type' : undefined,
          onAction: !hasSearchOrFilter ? hooks.handleAddNew : undefined,
        }}
        sortColumnId={sort.columnId}
        sortDirection={sort.direction}
        onSort={handleSort}
        actionsLabel="Actions"
        renderActions={(row) =>
          row.isEditing ? (
            <ActionButtons
              size="sm"
              actions={[
                {
                  id: 'save',
                  label: 'Save',
                  icon: (
                    <CheckmarkIcon
                      className="ds-action-button__svg"
                      aria-hidden="true"
                    />
                  ),
                  tone: 'primary',
                  variant: 'solid',
                  size: 'sm',
                  disabled: !row.name.trim(),
                  onClick: () => {
                    void hooks.handleSave(row.adjustmentTypeId)
                  },
                },
                {
                  id: 'cancel',
                  label: 'Cancel',
                  icon: (
                    <CloseIcon
                      className="ds-action-button__svg"
                      aria-hidden="true"
                    />
                  ),
                  tone: 'danger',
                  variant: 'outline',
                  size: 'sm',
                  onClick: () => hooks.handleCancel(row.adjustmentTypeId),
                },
              ]}
            />
          ) : (
            <SettingsRowActions
              onEdit={() => hooks.handleEdit(row.adjustmentTypeId)}
              onDelete={() => setPendingDelete(row)}
            />
          )
        }
        pagination={{
          currentPage: hooks.currentPage,
          totalPages: Math.max(
            1,
            Math.ceil(hooks.adjustmentCount / hooks.pageSize),
          ),
          pageSize: hooks.pageSize,
          pageSizeOptions: SETTINGS_PAGE_SIZE_OPTIONS,
          onPageChange: hooks.handlePageChange,
          onPageSizeChange: hooks.handlePageSizeChange,
        }}
      />

      <DeleteConfirmationModal
        show={!!pendingDelete}
        onHide={() => setPendingDelete(null)}
        onConfirm={() => {
          if (pendingDelete)
            void hooks.handleDelete(pendingDelete.adjustmentTypeId)
          setPendingDelete(null)
        }}
        entityName={pendingDelete?.name ?? ''}
        title="Delete adjustment type?"
        message="Are you sure you want to delete the adjustment type"
        confirmButtonText="Delete"
      />
    </div>
  )
}

export default Adjustments
